import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTable } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { Select, Store } from '@ngxs/store';
import { Module } from 'app/library/module/models/module.model';
import { ModuleState } from 'app/library/module/models/module.state';
import { ModuleUserProgressStatus } from 'app/library/module-user/enums/module-user-progress-status.enum';
import { ModuleUserApiService } from 'app/library/module-user/services/module-user.api-service';
import { ProgramItemService } from 'app/library/program-item/services/program-item.service';
import { ModuleCompletionSelectionType } from 'app/library/program-item/enums/module-completion-selection-type.enum';
import { ModuleType } from 'app/library/module/enums/moduleType.enum';
import { UserSectionsStatsState } from 'app/library/user-section/models/user-sections.state';
import { UserSectionService } from 'app/library/user-section/services/user-section.service';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { SnackBarTime } from 'app/projects/core/src/lib/constants/snack-bar';
import { ChartType } from 'chart.js';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { MaxBrainConfirmDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/confirm/confirm.component';
import { UserSectionStats } from 'app/library/user-section/models/user-section.model';
import { ParticipantsProgressDatasource } from './participants-progress-datasource';
import { ListParticipantsProgress } from './participants-progress.interface';

@Component({
    selector: 'app-participants-progress',
    templateUrl: './participants-progress.component.html',
    styleUrls: ['./participants-progress.component.scss'],
})
export class ParticipantsProgressDialogComponent extends UnsubscribeOnDestroy implements OnInit, AfterViewInit {
    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatTable) table!: MatTable<ListParticipantsProgress>;

    dataSource: ParticipantsProgressDatasource;

    /** Columns displayed in the table. Columns IDs can be added, removed, or reordered. */
    displayedColumns = ['description'];

    @Select(UserSectionsStatsState.getPercentage)
    percentage$: Observable<number>;

    /**
     * Chart information
     */
    doughnutChartData: {
        labels: string[];
        datasets: [
            {
                label: '';
                data: number[];
                backgroundColor: string[];
                hoverBackgroundColor: string[];
                hoverBorderColor: string[];
            }
        ];
    };
    doughnutChartType: ChartType = 'doughnut';
    doughnutChartOptions = {
        tooltips: { enabled: false },
        cutout: 60,
    };

    panelOpenState = false;
    numberOfCompletedSections: number;
    searchInput = new FormControl();

    // Create Angular Form
    private _formGroup: FormGroup;
    get formGroup(): FormGroup {
        return this._formGroup;
    }

    public controls = null;

    selectedStatus = null;
    selectedSearch = null;

    currentModule: Module;
    ModuleUserProgressStatus = ModuleUserProgressStatus;
    ModuleType = ModuleType;

    constructor(
        private _store: Store,
        public dialogRef: MatDialogRef<ParticipantsProgressDialogComponent>,
        private _formBuilder: FormBuilder,
        private _userSectionService: UserSectionService,
        private _programItemService: ProgramItemService,
        private _moduleUserApiService: ModuleUserApiService,
        private _matDialog: MatDialog,
        private _matSnackBar: MatSnackBar,
        private _translateService: TranslateService
    ) {
        super();
        this.dataSource = new ParticipantsProgressDatasource(_userSectionService);
        this.currentModule = this._store.selectSnapshot(ModuleState.getEntity);
    }

    ngOnInit(): void {
        this.dataSource.callBackApiFetch = this._userSectionService.fetchUserSectionStats;

        this._formGroup = this._formBuilder.group({
            statusInput: new FormControl(''),
        });

        this.controls = {
            statusInput: this._formGroup.get('statusInput'),
        };

        this.percentage$.pipe(takeUntil(this._unsubscribeAll)).subscribe((percentage) => {
            if (percentage) {
                percentage = Math.round(percentage * 100) / 100;
                this.doughnutChartData = {
                    labels: [''],
                    datasets: [
                        {
                            label: '',
                            data: [percentage, 100 - percentage],
                            backgroundColor: ['rgba(176, 207, 58, 1)', 'rgba(176, 207, 58, 0.3)'],
                            hoverBackgroundColor: ['rgba(176, 207, 58, 1)', 'rgba(176, 207, 58, 0.3)'],
                            hoverBorderColor: ['rgba(176, 207, 58, 1)', 'rgba(176, 207, 58, 0.3)'],
                        },
                    ],
                };
            }
        });

        this.searchInput.valueChanges.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this._unsubscribeAll)).subscribe((searchTerm) => {
            this.selectedSearch = searchTerm;
            this.dataSource.fetchUserSectionsStatsState(this.currentModule.id, this.paginator.pageSize, this.paginator.pageIndex + 1, this.selectedSearch, this.selectedStatus);
        });

        this.controls.statusInput.valueChanges.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this._unsubscribeAll)).subscribe((statusSelected) => {
            this.selectedStatus = statusSelected;
            this.dataSource.fetchUserSectionsStatsState(this.currentModule.id, this.paginator.pageSize, this.paginator.pageIndex + 1, this.selectedSearch, this.selectedStatus);
        });
    }

    ngAfterViewInit(): void {
        this.dataSource.paginator = this.paginator;
        this.table.dataSource = this.dataSource;

        this.paginator.page.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this._unsubscribeAll)).subscribe(({ pageIndex, pageSize }: PageEvent) => {
            this.dataSource.fetchUserSectionsStatsState(this.currentModule.id, pageSize, pageIndex + 1, this.selectedSearch, this.selectedStatus);
        });

        // Always load once view is ready so data appears even when stored pageSize equals the template default
        // (persistPageSize only emits a page event when the restored size differs).
        this.dataSource.fetchUserSectionsStatsState(
            this.currentModule.id,
            this.paginator.pageSize,
            this.paginator.pageIndex + 1,
            this.selectedSearch,
            this.selectedStatus
        );
    }

    getRowStatus(row: UserSectionStats): ModuleUserProgressStatus {
        if (!row?.total) {
            return ModuleUserProgressStatus.NotStarted;
        }

        if (row.completed >= row.total) {
            return ModuleUserProgressStatus.Completed;
        }

        if (row.completed > 0) {
            return ModuleUserProgressStatus.InProgress;
        }

        return ModuleUserProgressStatus.NotStarted;
    }

    async setParticipantCompletionToComplete(row: UserSectionStats): Promise<void> {
        if (this.getRowStatus(row) === ModuleUserProgressStatus.Completed) {
            return;
        }

        const isCompletionStatusChanged = await this._programItemService.changeProgramCompletionForParticipants({
            id: this.currentModule.id,
            user_ids: [row.user.id],
            status: ModuleUserProgressStatus.Completed,
            selection_type: ModuleCompletionSelectionType.Selected,
        });

        if (isCompletionStatusChanged) {
            const successMessage = this._translateService.instant('GENERAL.MESSAGE.SAVED');
            this._matSnackBar.open(successMessage, 'OK', {
                verticalPosition: 'top',
                duration: SnackBarTime.slow,
            });

            this.dataSource.fetchUserSectionsStatsState(this.currentModule.id, this.paginator.pageSize, this.paginator.pageIndex + 1, this.selectedSearch, this.selectedStatus);
        }
    }

    confirmResetParticipantProgress(row: UserSectionStats): void {
        if (this.getRowStatus(row) === ModuleUserProgressStatus.NotStarted) {
            return;
        }

        const confirmDialogRef = this._matDialog.open(MaxBrainConfirmDialogComponent, {
            disableClose: false,
        });

        confirmDialogRef.componentInstance.confirmTitle = 'GENERAL.BUTTON.CONFIRM';
        confirmDialogRef.componentInstance.confirmMessage = 'MODULE.PARTICIPANTS.MESSAGE.RESET_PROGRESS_CONFIRM';
        confirmDialogRef.componentInstance.confirmButtonColor = 'warn';
        confirmDialogRef.componentInstance.actionButtonText = 'MODULE.PARTICIPANTS.ACTION.RESET_PROGRESS';

        confirmDialogRef.afterClosed().subscribe(async (result) => {
            if (result) {
                await this._moduleUserApiService.resetModuleUserProgress(this.currentModule.id, row.user.id).toPromise();

                const successMessage = this._translateService.instant('GENERAL.MESSAGE.SAVED');
                this._matSnackBar.open(successMessage, 'OK', {
                    verticalPosition: 'top',
                    duration: SnackBarTime.slow,
                });

                this.dataSource.fetchUserSectionsStatsState(this.currentModule.id, this.paginator.pageSize, this.paginator.pageIndex + 1, this.selectedSearch, this.selectedStatus);
            }
        });
    }
}
